import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) {
      setErr("Le mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (pwd !== confirm) {
      setErr("Les mots de passe ne correspondent pas");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd, confirm_password: confirm });
      setDone(true);
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell eyebrow="Réinitialisation" title="Lien invalide.">
        <div className="rounded-3xl border border-[#F2C0B7] bg-[#FEEFEC] p-6 text-center text-sm text-[#A3483F]" data-testid="reset-no-token">
          Le lien de réinitialisation est invalide ou a expiré.
        </div>
        <Link to="/forgot-password">
          <Button className="mt-5 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12">Demander un nouveau lien</Button>
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell eyebrow="Mot de passe réinitialisé" title="C'est fait.">
        <div className="rounded-3xl border border-[#E6C29A] bg-gradient-to-br from-[#FAF3EA] to-white p-6 text-center" data-testid="reset-done">
          <CheckCircle2 className="w-10 h-10 text-[#C68E59] mx-auto mb-3" />
          <p className="text-sm text-[#1A1A1A]">Votre mot de passe a bien été modifié.</p>
        </div>
        <Button onClick={() => navigate("/login")} className="mt-5 w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="reset-go-login">
          Se connecter
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Réinitialisation" title="Choisissez un nouveau mot de passe." subtitle="Au moins 8 caractères. Évitez les mots simples.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div>
          <Label className="caption-label">Nouveau mot de passe</Label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 text-[#A26F3E] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} className="rounded-xl pl-10 pr-10 h-11" data-testid="reset-password" />
            <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#A26F3E]" tabIndex={-1}>
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="caption-label">Confirmer</Label>
          <Input type={showPwd ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="rounded-xl mt-2 h-11" data-testid="reset-confirm" />
        </div>
        {err && (
          <div className="rounded-xl bg-[#FEEFEC] border border-[#F2C0B7] text-[#A3483F] text-sm px-4 py-3">{err}</div>
        )}
        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white h-12" data-testid="reset-submit">
          {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mise à jour…</>) : "Mettre à jour mon mot de passe"}
        </Button>
      </form>
    </AuthShell>
  );
}
